import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import { TYPE_LABELS, TYPE_COLORS } from "../constants";

export default function DashboardTypeBreakdown({ typeBreakdown, leaveStats }) {
    return (
        <Card
            sx={{
                borderRadius: 4,
                boxShadow: "0 22px 44px rgba(15, 23, 42, 0.12)",
                border: "1px solid rgba(148, 163, 184, 0.28)",
                background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 248, 255, 0.92) 50%, rgba(255, 255, 255, 0.94) 100%)",
                backdropFilter: "blur(12px)",
                height: "100%",
                overflow: "hidden",
                position: "relative",
                transition:
                    "transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 30px 68px rgba(15, 23, 42, 0.18)",
                    borderColor: "rgba(148, 163, 184, 0.45)",
                },
            }}
        >
            {/* Accent top bar */}
            <Box
                sx={{
                    height: 6,
                    background:
                        "linear-gradient(90deg, #1d4ed8, rgba(255, 255, 255, 0.85), #0ea5e9)",
                }}
            />

            {/* Radial ambient glow */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(circle at 12% 12%, rgba(29, 78, 216, 0.08), transparent 50%),
                        radial-gradient(circle at 88% 12%, rgba(15, 23, 42, 0.04), transparent 45%),
                        radial-gradient(circle at 50% 100%, rgba(15, 23, 42, 0.03), transparent 55%)
                    `,
                    pointerEvents: "none",
                }}
            />

            <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: "-0.01em",
                        color: "rgba(15, 23, 42, 0.9)",
                    }}
                >
                    Répartition par type
                </Typography>
                <Divider
                    sx={{
                        my: 2,
                        borderColor: "rgba(148, 163, 184, 0.22)",
                    }}
                />
                <Stack spacing={2}>
                    {typeBreakdown.length ? (
                        typeBreakdown.map(([type, count]) => {
                            const barColor = TYPE_COLORS[type] || TYPE_COLORS.OTHER || "#94a3b8";
                            return (
                                <Box
                                    key={type}
                                    sx={{
                                        px: 2,
                                        py: 1.25,
                                        borderRadius: 2.5,
                                        transition:
                                            "background 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                        "&:hover": {
                                            background: `${barColor}08`,
                                            boxShadow: `0 2px 8px ${barColor}14`,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                color: "rgba(15, 23, 42, 0.8)",
                                                letterSpacing: "0.01em",
                                            }}
                                        >
                                            {TYPE_LABELS[type] || type}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                color: barColor,
                                                fontSize: "0.85rem",
                                            }}
                                        >
                                            {count}
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        value={
                                            leaveStats.total
                                                ? (count / leaveStats.total) * 100
                                                : 0
                                        }
                                        variant="determinate"
                                        sx={{
                                            mt: 1,
                                            height: 7,
                                            borderRadius: 999,
                                            backgroundColor: "rgba(148, 163, 184, 0.12)",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 999,
                                                background: `linear-gradient(90deg, ${barColor}, ${barColor}cc)`,
                                                transition:
                                                    "transform 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        })
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(100, 116, 139, 0.7)",
                                fontStyle: "italic",
                                py: 2,
                                textAlign: "center",
                            }}
                        >
                            Aucune donnée disponible.
                        </Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
